import { QueryParams } from 'common/urls/QueryParams';
import { difference, uniq } from 'lodash';
import { CuisineRegion } from 'src/gqlReactTypings.generated.d';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { ArrayParam, useQueryParam, withDefault } from 'use-query-params';

const cuisineRegions = new Set<string>(Object.values(CuisineRegion));

/*
  Summary: This hook helps replace the SEGMENT_FILTER query param with the CUISINE_FILTER query param when filtering by cuisine. 
  For example, http://localhost:3017/explore/zip/94105?c=CHINESE -> http://localhost:3017/explore/zip/94105?cuisine=CHINESE
  Explanation: Chef first experience supports filtering cuisine by SEGMENT_FILTER. However, we need to use CUISINE_FILTER
  for the dish first experience. When users click on a marketing link, a new url including segment filter query params is
  generated. We need this hook to make sure the filtering works if the user lands on the dish first experience.
*/
export const useCuisineSegmentMarketingUrlPatch = () => {
  const [segments, setSegments] = useQueryParam(QueryParams.SEGMENT_FILTER, withDefault(ArrayParam, []));
  const [cuisines, setCuisines] = useQueryParam(QueryParams.CUISINE_FILTER, withDefault(ArrayParam, []));

  const filterCuisineSegments = (segmentNames: (string | null)[] | never[]) =>
    segmentNames.filter(
      (segmentName): segmentName is CuisineRegion => !!segmentName && cuisineRegions.has(segmentName)
    );

  useOnMount(() => {
    if (cuisines.length) return;

    const cuisineSegments = filterCuisineSegments(segments);

    if (cuisineSegments.length === 0) return;

    const newSegments = difference(segments, cuisineSegments);
    const newCuisines = uniq([...cuisines, ...cuisineSegments]);
    setSegments(newSegments);
    setCuisines(newCuisines);
  });
};
